<script setup lang="ts">
import { ElNotification } from 'element-plus'
import {
  checkConnection,
  connectedGames,
  connectionCheckingLoading,
  connectionDialogGameKey,
  isRequired,
  visibilityConnectDialog
} from '@/components/goat/page/home/ConnectGameDialog'
import { GameKeyEnum } from '@/types/enums'

const { t } = useI18n()
const { games } = siteUtils
const showHowMode = ref(false)
function beforeClose(done: () => void) {
  if (showHowMode.value) {
    showHowMode.value = false
  } else {
    done()
  }
}
function onClose() {
  isRequired.value = false
  connectionDialogGameKey.value = null
}

const gameSettings = computed(() => {
  if (connectionDialogGameKey.value) {
    return games?.[connectionDialogGameKey.value]
  }
  return null
})
const { gaVerifyBtnClick } = gaUtils
async function handleVerify() {
  await checkConnection(connectionDialogGameKey.value, false, { appendTo: '#connect-dialog-body' })
  gaVerifyBtnClick({ gs_game: connectionDialogGameKey.value || '', gs_context: `connected=${!!connectedGames.value?.[connectionDialogGameKey.value || '']?.id}` })
  if (connectionDialogGameKey.value && !connectedGames.value?.[connectionDialogGameKey.value]?.id) {
    ElNotification.closeAll()
    ElNotification({
      message: t('connectGame.notify.notConnected'),
      type: 'warning',
      duration: 5000,
      offset: 0,
      appendTo: '#connect-dialog-body',
      customClass: 'right-1/2 translate-x-1/2 large absolute'
    })
    return false
  }
  visibilityConnectDialog.value = false
}
</script>

<template>
  <el-dialog
    v-model="visibilityConnectDialog"
    align-center
    modal-class="bg-black/80"
    class="goat-connect-dialog rounded-2xl w-11/12 bg-white text-center overflow-hidden"
    :class="{ 'big:max-w-2xl': !showHowMode, 'big:max-w-3xl': showHowMode }"
    destroy-on-close
    :before-close="beforeClose"
    @close="onClose()"
  >
    <template #header>
      <div class="uppercase text-2xl flex justify-center items-center tracking-wider font-extrabold p-5 bg-gs-body-bg">
        <div class="text-gs-bg">
          {{ gameSettings?.name }}
        </div>
        <el-image
          :src="gameSettings?.icon"
          class="rounded-xl shadow-md shadow-gs-bg w-14 mx-4"
        />
      </div>
    </template>
    <template #default>
      <div class="bg-white pt-3 big:pt-5 px-5 big:px-10 max-w-lg big:max-w-none mx-auto">
        <div class="font-extrabold text-2xl uppercase">
          <span v-if="showHowMode">{{ t('connectGame.howTo') }}</span>
          <div v-else-if="isRequired">
            {{ t('connectGame.firstConnect') }}
          </div>
          {{ t('connectGame.title') }}
        </div>
        <template v-if="showHowMode">
          <div class="mx-1 big:mx-4 mt-5 mb-16 flex flex-wrap">
            <ul class="text-left list-decimal text-sm big:text-base font-medium big:w-1/2 mb-5">
              <li>{{ t('connectGame.steps.step1', { game: gameSettings?.name }) }}</li>
              <li>{{ gameSettings?.gameKey === GameKeyEnum.questland ? t('connectGame.steps.step2Questland') : t('connectGame.steps.step2Other') }}</li>
              <li>{{ t('connectGame.steps.step3') }}</li>
              <li>{{ t('connectGame.steps.step4') }}</li>
              <li>{{ t('connectGame.steps.step5') }}</li>
            </ul>
            <div
              v-if="gameSettings?.connectInImg?.length"
              class="big:w-1/2 flex justify-center items-center flex-col w-full"
            >
              <el-image
                width="710"
                height="768"
                class="px-2 mt-2 max-w-40 big:max-w-none"
                :src="gameSettings?.connectInImg[0]"
                :zoom-rate="0"
                :max-scale="1"
                :min-scale="1"
                :preview-src-list="gameSettings?.connectInImg"
                fit="cover"
              />
              <div class="text-sm text-neutral-150 w-full italic tracking-wide">
                {{ t('connectGame.viewPreview') }} <br>{{ t('connectGame.screenshots') }}
              </div>
            </div>
            <div class="clear-both w-full">
              <el-button
                color="#3ba6bc"
                class="text-white tracking-wide rounded-lg uppercase text-lg mini:text-xl mt-5 px-10"
                size="large"
                style="--el-button-size: 2.5em"
                @click="showHowMode = false"
              >
                {{ t('connectGame.backBtn') }}
              </el-button>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            id="connect-dialog-body"
            class="mt-3 big:mt-5 mb-8 big:mb-12 relative"
          >
            <div
              class="text-sm big:text-xl"
            >
              {{ t('connectGame.description') }}
              [<a
                href="#"
                class="text-gs-main-color text-lg big:text-xl font-bold hover:text-cyan-600 inline whitespace-nowrap"
                @click="showHowMode = true"
              >{{ t('connectGame.clickHere') }}</a>] {{ t('connectGame.detailedInstructions') }}
              {{ t('connectGame.thenReturn') }}
            </div>
            <el-button
              color="#3ba6bc"
              class="text-white tracking-wide rounded-lg uppercase text-lg mini:text-xl mt-5 px-10"
              size="large"
              style="--el-button-size: 2.5em"
              :loading="!!(connectionDialogGameKey && connectionCheckingLoading?.[connectionDialogGameKey])"
              @click="handleVerify"
            >
              {{ t('connectGame.verifyBtn') }}
            </el-button>
          </div>
        </template>
      </div>
    </template>
  </el-dialog>
</template>

<style>
@layer components {
  .goat-connect-dialog {
    --el-message-close-size: 1.5rem;
    --el-dialog-padding-primary: 0.1;

    .el-dialog__header {
      margin-right: 0 !important;
    }

    .el-input__inner {
      --el-input-inner-height: 48px;
    }

    .el-image-viewer__actions {
      display: none !important;
    }
  }
}
</style>
